import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Box, 
  useTheme, 
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';
import digisparkLogo from '../assets/logo-1.png';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = ({ toggleColorMode }) => {
  const theme = useTheme();
  const location = useLocation();
  // Extract language from the current URL path
  const match = location.pathname.match(/^\/(en|fr)(\/|$)/);
  const currentLang = match ? match[1] : 'en';
  const { t } = useTranslation();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Function to determine if a path is active
  const isActive = (path) => {
    const currentPath = location.pathname;
    const languagePath = `/${currentLang || 'en'}`;
    
    if (path === '/' && currentPath === languagePath) {
      return true;
    }
    if (path !== '/' && currentPath.startsWith(`${languagePath}${path}`)) {
      return true;
    }
    return false;
  };

  // Function to generate language-aware links
  const getLanguagePath = (path) => {
    return `/${currentLang}${path}`;
  };

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navItems = [
    { name: t('navbar.whoWeAre'), path: '/about' },
    { name: t('navbar.ourWork'), path: '/programs' },
    { name: t('navbar.getInvolved'), path: '/contact' },
  ];

  // Add donate button as a separate prominent element
  const donateButton = (
    <Button
      component={Link}
      to={getLanguagePath('/donate')}
      variant="contained"
      color="primary"
      sx={{
        fontWeight: 800,
        ml: 1,
        borderRadius: 0, 
        px: 2.5,
        py: 1.2,
        fontSize: '1.1rem',
        boxShadow: theme.palette.mode === 'dark' 
          ? '0px 4px 10px rgba(0, 0, 0, 0.3)' 
          : '0px 4px 10px rgba(22, 56, 50, 0.2)',
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0px 6px 15px rgba(0, 0, 0, 0.4)' 
            : '0px 6px 15px rgba(22, 56, 50, 0.3)',
        }
      }}
    >
      {t('navbar.donate')}
    </Button>
  );

  const mobileDrawer = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleDrawerToggle}
      sx={{
        '& .MuiDrawer-paper': { 
          width: 240,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.default
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {t('navbar.digisparkFoundation')}
        </Typography>
        <List>
          {navItems.map((item) => (
            <ListItem 
              key={item.name} 
              component={Link} 
              to={getLanguagePath(item.path)}
              onClick={handleDrawerToggle}
              sx={{ 
                pl: 2,
                py: 1.5,
                borderLeft: isActive(item.path) 
                  ? `4px solid ${theme.palette.primary.main}` 
                  : '4px solid transparent',
                backgroundColor: isActive(item.path) 
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(22, 56, 50, 0.05)'
                  : 'transparent',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(22, 56, 50, 0.1)',
                  borderLeft: `4px solid ${theme.palette.primary.light}`,
                },
                color: isActive(item.path) 
                  ? theme.palette.mode === 'dark' 
                    ? '#ffffff' 
                    : theme.palette.primary.main 
                  : 'inherit',
                textDecoration: 'none'
              }}
            >
              <ListItemText 
                primary={item.name} 
                primaryTypographyProps={{ 
                  fontWeight: isActive(item.path) ? 700 : 500,
                  fontSize: '0.9rem'
                }}
              />
            </ListItem>
          ))}
          
          {/* Donate button in mobile drawer */}
          <ListItem 
            component={Link} 
            to={getLanguagePath('/donate')}
            onClick={handleDrawerToggle}
            sx={{ 
              mt: 2,
              py: 2,
              borderRadius: 0,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              textDecoration: 'none'
            }}
          >
            <ListItemText 
              primary={t('navbar.donate')} 
              primaryTypographyProps={{ 
                fontWeight: 800,
                textAlign: 'center',
                fontSize: '1.2rem'
              }}
            />
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );

  return (
    <AppBar 
      position="sticky"
      color="default"
      elevation={0}
      sx={{ 
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(18, 18, 18, 0.85)' 
          : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ py: isMobile ? 2 : 3 }}>
          <Box component={Link} to={getLanguagePath('/')} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit', flexGrow: 1 }}>
            <Box 
              component="img" 
              src={digisparkLogo} 
              alt="Digispark Foundation" 
              sx={{ 
                height: isMobile ? 55 : 80,
                mr: isMobile ? 1 : 2 
              }} 
            />
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              sx={{ 
                fontWeight: 800, 
                fontSize: isMobile ? '1.3rem' : '1.6rem',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {t('navbar.digisparkFoundation')}
            </Typography>
          </Box>
          
          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {navItems.map((item) => (
              <Button 
                key={item.name}
                component={Link} 
                to={getLanguagePath(item.path)}
                color="inherit" 
                sx={{ 
                  fontWeight: isActive(item.path) ? 900 : 700,
                  mx: 1,
                  py: 1.2,
                  fontSize: '0.95rem',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  color: isActive(item.path) 
                    ? theme.palette.mode === 'dark'
                      ? '#ffffff'
                      : theme.palette.primary.main
                    : 'inherit',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: isActive(item.path) ? '100%' : '0%',
                    height: '4px',
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '2px 2px 0 0',
                    transition: 'width 0.3s ease',
                    opacity: isActive(item.path) ? 1 : 0,
                    boxShadow: isActive(item.path) ? `0 4px 8px -2px ${theme.palette.primary.main}40` : 'none',
                  },
                  '&:hover': {
                    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                    '&::after': {
                      width: '100%',
                      opacity: 0.7,
                    },
                  }
                }}
              >
                {item.name}
              </Button>
            ))}
            
            {donateButton}
            
            <Box sx={{ ml: 1 }}>
              <LanguageSwitcher />
            </Box>
            
            <IconButton 
              onClick={toggleColorMode} 
              color="inherit" 
              sx={{ 
                ml: 1,
                p: 1.5,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.03)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>
          
          {/* Mobile Navigation */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <LanguageSwitcher />
            <IconButton 
              onClick={toggleColorMode} 
              color="inherit" 
              sx={{ mr: 1 }}
            >
              {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          
          {/* Mobile Menu Drawer */}
          {mobileDrawer}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 